import { apiClient } from './http.api';

/**
 * Report API
 * 
 * Functions for generating and managing reports
 */
export const reportApi = {
  /**
   * Regenerate report (delete old report and prepare for new generation)
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Response with message and session_id
   */
  regenerateReport: async (sessionId) => {
    const response = await apiClient.post(
      `/api/reports/sessions/${sessionId}/regenerate-report`
    );
    return response.data;
  },

  /**
   * Generate session report
   * @param {string} sessionId - Session ID
   * @param {Object} options - Optional parameters
   * @param {boolean} options.auto_send_faicon - Automatically send to Faicon after generation
   * @param {boolean} options.auto_send_digwatch - Automatically send to DigWatch after generation
   * @param {string} options.llm_model - LLM model to use (e.g., 'claude-sonnet-4-6', 'gpt-4o')
   * @param {Object} options.custom_prompts - Custom prompts to override defaults (key-value pairs of prompt names and texts)
   * @returns {Promise<Object>} Report response with report_id and status
   */
  generateSessionReport: async (sessionId, options = {}) => {
    const response = await apiClient.post(
      `/api/reports/sessions/${sessionId}/generate`,
      options
    );
    return response.data;
  },

  /**
   * Get report configuration (editable prompts, available models, default prompts)
   * @returns {Promise<Object>} Report configuration object
   */
  getReportConfig: async () => {
    const response = await apiClient.get('/api/reports/config');
    return response.data;
  },

  /**
   * Get available LLM models
   * @returns {Array<Object>} List of available models with their details
   * @deprecated Use getReportConfig() instead
   */
  getAvailableModels: () => {
    // Hardcoded list - fallback if API is not available
    return [
      // Anthropic/Claude is disabled for report generation selection.
      // Keep this block here as a quick rollback point if we re-enable it later.
      // {
      //   value: 'claude-sonnet-4-6',
      //   label: 'Claude Sonnet 4.6',
      //   provider: 'Anthropic',
      //   description: 'Default model for high-quality report generation',
      //   isDefault: true
      // },
      {
        value: 'openai/gpt-oss-120b',
        label: 'GPT-OSS 120B (Local)',
        provider: 'ChatGPT-OSS',
        description: 'Local 120B model on chatgpt-oss.diplomacy.edu - tries first, then backend can fall back to GPT-5.4',
        isDefault: true
      },
      {
        value: 'gpt-5.4',
        label: 'GPT-5.4',
        provider: 'OpenAI',
        description: 'Fallback/default external model for report generation via OpenAI API',
        isDefault: false
      },
      {
        value: 'gpt-4o',
        label: 'GPT-4o',
        provider: 'OpenAI',
        description: 'Alternative model for report generation',
        isDefault: false
      }
    ];
  },

  /**
   * Generate event report
   * @param {string} eventId - Event ID
   * @param {Object} options - Optional parameters
   * @returns {Promise<Object>} Report response with report_id and status
   */
  generateEventReport: async (eventId, options = {}) => {
    const response = await apiClient.post(
      `/api/reports/events/${eventId}/generate`,
      options
    );
    return response.data;
  },

  /**
   * Get session report status
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Report status information
   */
  getSessionReportStatus: async (sessionId) => {
    const response = await apiClient.get(`/api/reports/sessions/${sessionId}/report/status`);
    return response.data;
  },

  /**
   * Get event report status
   * @param {string} eventId - Event ID
   * @returns {Promise<Object>} Report status information
   */
  getEventReportStatus: async (eventId) => {
    const response = await apiClient.get(`/api/reports/events/${eventId}/report/status`);
    return response.data;
  },

  /**
   * Get session report content
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Report content with data
   */
  getSessionReport: async (sessionId) => {
    const response = await apiClient.get(`/api/reports/sessions/${sessionId}/report`);
    return response.data;
  },

  /**
   * Get event report content
   * @param {string} eventId - Event ID
   * @returns {Promise<Object>} Report content with data
   */
  getEventReport: async (eventId) => {
    const response = await apiClient.get(`/api/reports/events/${eventId}/report`);
    return response.data;
  },

  /**
   * Get report status (legacy - kept for backward compatibility)
   * @param {string} reportId - Report ID
   * @returns {Promise<Object>} Report status information
   */
  getReportStatus: async (reportId) => {
    const response = await apiClient.get(`/api/reports/${reportId}/status`);
    return response.data;
  },

  /**
   * Publish report to FAICON
   * @param {string} sessionId - Session ID
   * @param {string} lang - ISO 639-1 language code (default 'en')
   * @returns {Promise<Object>} Response with published_url
   */
  publishToFaicon: async (sessionId, lang = 'en') => {
    const response = await apiClient.post(
      `/api/reports/sessions/${sessionId}/send-faicon`,
      null,
      { params: { lang } }
    );
    return response.data;
  },

  /**
   * Publish report to WordPress
   * @param {string} sessionId - Session ID
   * @param {string} lang - ISO 639-1 language code (default 'en')
   * @returns {Promise<Object>} Response with published_url
   */
  publishToWordPress: async (sessionId, lang = 'en') => {
    const response = await apiClient.post(
      `/api/reports/sessions/${sessionId}/send-digwatch`,
      null,
      { params: { lang } }
    );
    return response.data;
  },

  /**
   * Download session report as PDF
   * @param {string} sessionId - Session ID
   * @param {string} filename - Optional filename for the downloaded file
   * @param {string} lang - Optional ISO 639-1 language code (default 'en').
   *   When non-English, the backend loads the translated report content.
   * @returns {Promise<void>} Triggers file download
   */
  downloadSessionReportPdf: async (sessionId, filename = null, lang = 'en') => {
    const response = await apiClient.get(
      `/api/reports/sessions/${sessionId}/report/pdf`,
      {
        responseType: 'blob',
        params: { lang },
      }
    );
    
    // Extract filename from Content-Disposition header if not provided
    if (!filename) {
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      if (!filename) {
        const suffix = lang && lang !== 'en' ? `_${lang}` : '';
        filename = `session_report_${sessionId}${suffix}.pdf`;
      }
    }
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Defer revoke so Chrome/Firefox have time to start the download after
    // click(). Revoking synchronously can cancel the download on slow hosts.
    setTimeout(() => window.URL.revokeObjectURL(url), 2000);
  },

  /**
   * Download a ZIP bundle containing one PDF per language.
   *
   * Using a single ZIP instead of N sequential downloads avoids browser
   * "Site wants to download multiple files" prompts that were silently
   * swallowing one of the PDFs (typically the first one).
   *
   * @param {string} sessionId - Session ID
   * @param {string[]} [langs] - Optional list of ISO 639-1 codes. When empty
   *   or omitted, the backend auto-includes English + every completed
   *   translation.
   * @param {string} [filename] - Optional override for the downloaded file.
   * @returns {Promise<{included: string[], failed: string[]}>}
   *   Resolves with the language codes the backend actually packaged and
   *   any it skipped due to per-language errors.
   */
  downloadSessionReportPdfBundle: async (sessionId, langs = null, filename = null) => {
    const params = {};
    if (Array.isArray(langs) && langs.length > 0) {
      params.langs = langs.join(',');
    }

    const response = await apiClient.get(
      `/api/reports/sessions/${sessionId}/report/pdf-bundle`,
      {
        responseType: 'blob',
        params,
      }
    );

    if (!filename) {
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      if (!filename) {
        filename = `session_report_${sessionId}_bundle.zip`;
      }
    }

    const included = (response.headers['x-pdf-languages-included'] || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const failed = (response.headers['x-pdf-languages-failed'] || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const blob = new Blob([response.data], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(url), 2000);

    return { included, failed };
  },

  /**
   * Trigger report translation to a target language
   * @param {string} sessionId - Session ID
   * @param {string} lang - ISO 639-1 language code
   * @returns {Promise<Object>} Response with task_id and status
   */
  translateReport: async (sessionId, lang) => {
    const response = await apiClient.post(
      `/api/reports/sessions/${sessionId}/translate-report`,
      null,
      { params: { lang } }
    );
    return response.data;
  },

  /**
   * Get available report translation languages for a session
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Response with languages array
   */
  getReportLanguages: async (sessionId) => {
    const response = await apiClient.get(
      `/api/reports/sessions/${sessionId}/report-languages`
    );
    return response.data;
  },

  /**
   * Delete a translation for a session language
   * @param {string} sessionId - Session ID
   * @param {string} lang - ISO 639-1 language code
   * @returns {Promise<Object>} Response with confirmation
   */
  deleteTranslation: async (sessionId, lang) => {
    const response = await apiClient.delete(
      `/api/reports/sessions/${sessionId}/translations/${lang}`
    );
    return response.data;
  },

  /**
   * Cancel an in-progress translation task
   * @param {string} sessionId - Session ID
   * @param {string} lang - ISO 639-1 language code
   * @returns {Promise<Object>} Response with confirmation
   */
  cancelTranslation: async (sessionId, lang) => {
    const response = await apiClient.post(
      `/api/reports/sessions/${sessionId}/translations/${lang}/cancel`
    );
    return response.data;
  },

  /**
   * Download event report as PDF
   * @param {string} eventId - Event ID
   * @param {string} filename - Optional filename for the downloaded file
   * @returns {Promise<void>} Triggers file download
   */
  downloadEventReportPdf: async (eventId, filename = null) => {
    const response = await apiClient.get(
      `/api/reports/events/${eventId}/report/pdf`,
      { responseType: 'blob' }
    );
    
    // Extract filename from Content-Disposition header if not provided
    if (!filename) {
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      if (!filename) {
        filename = `event_report_${eventId}.pdf`;
      }
    }
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};

